import React, { useCallback, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import ActionButtonsSettings from "./ActionButtonsSettings";
import Image from "../common/Image";
import EmailSettingsSection from "./EmailSettingsSection";
import { useLayoutContext } from "../../context/LayoutContext";

const GlobalSettings = () => {
  const {
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    loading,
    setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
    page,
    setPage,
  } = useLayoutContext();
  const { saveProfile } = useAuth();
  const [internalUserSettings, setInternalUserSettings] = useState(
    userSettings ||
      ({
        generalSettings: {} as GeneralSettings,
        profile: {} as Profile,
      } as ProfileAndSettings),
  );
  const [file, setFile] = useState<File | null>(null);

  const setInternalSettings = (settings: typeof userSettings) => {
    setInternalUserSettings(settings as typeof internalUserSettings);
    setUserSettings(settings);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!userSettings) {
      setError("Por favor, carregue as configurações do utilizador.");
      return;
    }
    const smtpConfig = userSettings.generalSettings?.smtp_configuration;
    if (userSettings.generalSettings?.smtp_settings_changed) {
      if (!smtpConfig) {
        setError(
          "Por favor, configure as definições de SMTP antes de guardar.",
        );
        return;
      }
      if (
        smtpConfig.use_custom_email &&
        !userSettings.generalSettings?.smtp_settings_valid
      ) {
        setError("Por favor, teste as configurações de SMTP antes de guardar.");
        return;
      }
      if (!smtpConfig.use_custom_email && !smtpConfig.reply_to_email) {
        setError(
          "Por favor, preencha o campo 'Email de Resposta' nas configurações de SMTP.",
        );
        return;
      }
      if (!smtpConfig.use_custom_email && !smtpConfig.reply_to_name) {
        setError(
          "Por favor, preencha o campo 'Nome do Endereço de Resposta' nas configurações de SMTP.",
        );
        return;
      }
    }

    setError(null);
    try {
      const updatedSettings = userSettings as ProfileAndSettings;
      if (!updatedSettings) {
        console.error("User settings not loaded");
        return;
      }

      console.log("Updated user settings:", updatedSettings);
      const returnUpdatedSettings = await saveProfile(updatedSettings, file);
      setUserSettings(returnUpdatedSettings);
      setSuccess("Configurações atualizadas com sucesso!");
    } catch (error) {
      console.warn("Error updating settings:", error);
      setError("Failed to update settings. Please try again later.");
    } finally {
      setLoading(null);
    }
  };

  const handleSmtpConfigurationChange = (
    smtpSettings: SmtpCustomConfiguration,
  ) => {
    if (!userSettings) return;

    const updatedSettings = {
      ...userSettings,
      generalSettings: {
        ...userSettings.generalSettings,
        smtp_configuration: smtpSettings,
        company_logo_location: null,
        smtp_settings_changed: true,
        smtp_settings_valid: null, // Reset validation status
      },
      profile: userSettings?.profile || {},
    };
    // @ts-ignore
    setUserSettings(updatedSettings);
  };

  const defaultUserSmtpConfiguration = (userSettings: ProfileAndSettings) => {
    return {
      use_custom_email: false,
      reply_to_email: userSettings.profile?.email,
      reply_to_name:
        userSettings.profile.firstName + " " + userSettings.profile.lastName,
      from_email: userSettings.profile?.email,
      from_name:
        userSettings.profile.firstName + " " + userSettings.profile.lastName,
      smtp_authentication: true,
    } as SmtpCustomConfiguration;
  };

  if (!userSettings) {
    return <div>Loading...</div>;
  }

  const renderMarginThresholds = () => {
    const readOnly = userSettings.generalSettings?.use_default_margin ?? true;
    const thresholds =
      internalUserSettings?.generalSettings?.margin_thresholds || [];
    return (
      <div className="field-body pl-5">
        <div className="control w-full">
          {thresholds.map(
            (
              threshold: {
                from: number | null;
                to: number | null;
                margin: number;
              },
              idx: number,
            ) => (
              <div className="is-flex mb-2" key={idx}>
                <span className="mr-2">De</span>
                <input
                  type="number"
                  className="input mr-2"
                  value={idx > 0 ? (thresholds[idx - 1].to ?? "") : 0}
                  style={{ width: 120 }}
                  disabled={true}
                />
                <span className="mr-2">até</span>
                <input
                  type="number"
                  className="input mr-2"
                  placeholder={idx === thresholds.length - 1 ? "∞" : ""}
                  value={threshold.to || ""}
                  min={(threshold.from ?? 0) + 1}
                  step={0.01}
                  onChange={(e) => {
                    const value =
                      e.target.value === "" ? null : parseFloat(e.target.value);
                    const updated = [...thresholds];
                    updated[idx].to = (value || 0) > 0 ? value : null;
                    if (idx < thresholds.length - 1 && value !== null) {
                      updated[idx + 1].from = value + 0.01;
                    }

                    setInternalSettings({
                      ...userSettings,
                      generalSettings: {
                        ...userSettings.generalSettings,
                        margin_thresholds: updated,
                        company_logo_location: null,
                      },
                    });
                  }}
                  style={{ width: 120 }}
                  disabled={
                    readOnly ||
                    (threshold.to === null && idx === thresholds.length - 1)
                  }
                />
                <span className="mr-2">aplicar</span>
                <input
                  type="number"
                  className="input mr-2"
                  placeholder="%"
                  value={threshold.margin}
                  min={0}
                  max={100}
                  step={0.01}
                  disabled={readOnly}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value)) {
                      const updated = [...thresholds];
                      updated[idx].margin = value;
                      setInternalSettings({
                        ...userSettings,
                        generalSettings: {
                          ...userSettings.generalSettings,
                          margin_thresholds: updated,
                          company_logo_location: null,
                        },
                      });
                    }
                  }}
                  style={{ width: 80 }}
                />
                <span className="mr-2">%</span>
                {thresholds.length > 1 && (
                  <button
                    type="button"
                    className="button is-small is-danger is-light ml-2"
                    disabled={readOnly}
                    onClick={() => {
                      const updated = [...thresholds];
                      updated.splice(idx, 1);
                      if (updated[idx] && idx > 0) {
                        updated[idx].from = (updated[idx - 1].to || 0) + 0.01;
                      }
                      if (idx === thresholds.length - 1 && updated[idx - 1]) {
                        updated[idx - 1].to = null;
                      }
                      if (idx === 0 && updated[0]) {
                        updated[0].from = 0;
                      }

                      setInternalSettings({
                        ...userSettings,
                        generalSettings: {
                          ...userSettings.generalSettings,
                          margin_thresholds: updated,
                          company_logo_location: null,
                        },
                      });
                    }}
                  >
                    Remover
                  </button>
                )}
              </div>
            ),
          )}
          <button
            type="button"
            className="button is-small is-link mt-2"
            disabled={readOnly}
            onClick={() => {
              const updated = [
                ...thresholds,
                {
                  from:
                    (thresholds.length || 0) > 0
                      ? (thresholds[(thresholds.length || 0) - 1].to ?? 0) +
                        0.01
                      : 0,
                  to: null,
                  margin: 0,
                },
              ];
              setInternalSettings({
                ...userSettings,
                generalSettings: {
                  ...userSettings.generalSettings,
                  margin_thresholds: updated,
                  company_logo_location: null,
                },
              });
            }}
          >
            Adicionar Threshold
          </button>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="box">
        <fieldset>
          <legend className="title is-4">Logotipo</legend>

          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">Logótipo da Empresa</label>
              <span className="tip text-xs has-text-grey">
                O logotipo será utilizado em propostas e documentos
              </span>
            </div>
            <div className="field-body">
              <div className="file has-name">
                <label className="file-label">
                  <input
                    className="file-input"
                    type="file"
                    name="companyLogo"
                    accept="image/*"
                    onChange={(e) => {
                      const selectedFile = e.target.files?.[0];
                      if (selectedFile) {
                        setFile(selectedFile);
                      }
                    }}
                  />
                  <span className="file-cta">
                    <span className="file-label">Escolher ficheiro...</span>
                  </span>
                  <span className="file-name">
                    {file ? file.name : "Nenhum ficheiro selecionado"}
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4"></div>
            <div className="field-body pl-5">
              <div className="control">
                {file ? (
                  <div style={{ position: "relative" }}>
                    <picture>
                      <img
                        src={URL.createObjectURL(file)}
                        alt="Selected Logo"
                        className="mt-2"
                        style={{ maxWidth: "200px", maxHeight: "100px" }}
                      />
                    </picture>
                    <span
                      className="tag is-info is-light"
                      style={{ position: "absolute", top: "8px", right: "8px" }}
                    >
                      Preview
                    </span>
                  </div>
                ) : userSettings.generalSettings?.company_logo_location ? (
                  <div className="image-container  max-w-[150px] max-h-[100px]">
                    <Image
                      src="http://iapc.pt/images/150/logo-IAPC.jpg"
                      dataSrc={
                        userSettings?.generalSettings?.company_logo_location
                      }
                      loading="progressive"
                      sizes="150"
                      alt="Logo"
                      className="object-cover w-full h-10"
                    />
                  </div>
                ) : (
                  <p className="mt-2 has-text-grey">
                    Nenhum logotipo selecionado
                  </p>
                )}
              </div>
            </div>
          </div>
        </fieldset>
      </div>
      <div className="box">
        <EmailSettingsSection
          smtpSettings={
            userSettings?.generalSettings?.smtp_configuration ??
            defaultUserSmtpConfiguration(userSettings)
          }
          setSmtpSettings={handleSmtpConfigurationChange}
          smtpSettingsChanged={
            userSettings?.generalSettings?.smtp_settings_changed ?? false
          }
          successTest={
            userSettings?.generalSettings?.smtp_settings_valid ?? null
          }
          setSuccessTest={(success) => {
            const updatedSettings = {
              ...userSettings,
              generalSettings: {
                ...userSettings.generalSettings,
                smtp_settings_valid: success,
                companyLogoLocation: null,
              },
              profile: userSettings?.profile || {},
            };
            // @ts-ignore
            setUserSettings(updatedSettings);
          }}
        />
      </div>

      <div className="box">
        <fieldset>
          <legend className="title is-4">Configurações de IVA</legend>

          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label" htmlFor="defaultLanguage">
                Taxa de IVA
              </label>
              <span className="tip text-xs has-text-grey">
                Taxa de IVA aplicada automaticamente nos cálculos de preço
              </span>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control">
                  <div className="select w-[380px]">
                    <select
                      name="iva_percentage"
                      value={userSettings.generalSettings?.vat_percentage ?? 23}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value)) {
                          const updatedSettings = {
                            ...userSettings,
                            generalSettings: {
                              ...userSettings.generalSettings,
                              vat_percentage: value,
                              company_logo_location: null,
                            },
                            profile: userSettings?.profile || {},
                          };
                          // @ts-ignore
                          setUserSettings(updatedSettings);
                        }
                      }}
                    >
                      <option value="23">
                        Taxa de 23% (Portugal Continental)
                      </option>
                      <option value="22">
                        Taxa de 22% (Região Autónoma da Madeira)
                      </option>
                      <option value="16">
                        Taxa de 16% (Região Autónoma dos Açores)
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      <div className="box">
        <fieldset>
          <legend className="title is-4">Configurações de Margem</legend>

          <div className="field is-horizontal">
            <div className="field-label is-normal  is-flex-grow-4">
              <label className="label" htmlFor="useRecommendedMargin">
                Utilizar Margem Recomendada
              </label>
              <span className="tip text-xs has-text-grey">
                Aplica automaticamente as margens sugeridas pelo sistema
              </span>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control">
                  <input
                    type="checkbox"
                    name="useRecommendedMargin"
                    className="mr-2"
                    checked={
                      userSettings.generalSettings?.use_default_margin ?? true
                    }
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const updatedSettings = {
                        ...userSettings,
                        generalSettings: {
                          ...userSettings.generalSettings,
                          use_default_margin: checked,
                          company_logo_location: null,
                        },
                        profile: userSettings?.profile || {},
                      };
                      // @ts-ignore
                      setUserSettings(updatedSettings);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            className={`field is-horizontal ${(userSettings.generalSettings?.use_default_margin ?? true) ? " opacity-50 cursor-not-allowed " : ""}`}
          >
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">
                Configuração de Margens por Tabela de Preços
              </label>
              <span className="tip text-xs has-text-grey">
                Defina os thresholds de preço e as margens a aplicar
              </span>
            </div>
            {renderMarginThresholds()}
          </div>
        </fieldset>
      </div>

      <div className="box">
        <fieldset>
          <legend className="title is-4">Configurações Adicionais</legend>
          <div className="field is-horizontal">
            <div className="field-label is-normal  is-flex-grow-4">
              <label className="label" htmlFor="enableViewCostPrices">
                Mostrar preços de custo
              </label>
              <span className="tip text-xs has-text-grey">
                Habilita funcionalidades visualização de preços de custo
              </span>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control">
                  <label className="checkbox mt-2">
                    <input
                      type="checkbox"
                      name="enableViewCostPrices"
                      className="mr-2"
                      checked={
                        userSettings.generalSettings?.show_cost_price ?? true
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const updatedSettings = {
                          ...userSettings,
                          generalSettings: {
                            ...userSettings.generalSettings,
                            show_cost_price: checked,
                            company_logo_location: null,
                          },
                          profile: userSettings?.profile || {},
                        } as ProfileAndSettings;
                        setUserSettings(updatedSettings);
                      }}
                    />
                    Mostrar preços de custo
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="field is-horizontal">
            <div className="field-label is-normal  is-flex-grow-4">
              <label className="label" htmlFor="enableAdvancedMode">
                Ativar Modo Avançado
              </label>
              <span className="tip text-xs has-text-grey">
                Habilita funcionalidades avançadas de edição de preços
              </span>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control">
                  <label className="checkbox mt-2">
                    <input
                      type="checkbox"
                      name="enableAdvancedMode"
                      className="mr-2"
                      checked={
                        userSettings.generalSettings?.show_margin_slider ?? true
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const updatedSettings = {
                          ...userSettings,
                          generalSettings: {
                            ...userSettings.generalSettings,
                            show_margin_slider: checked,
                            company_logo_location: null,
                          },
                          profile: userSettings?.profile || {},
                        };
                        // @ts-ignore
                        setUserSettings(updatedSettings);
                      }}
                    />
                    Configurador de margem
                  </label>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </div>
      <ActionButtonsSettings />
    </form>
  );
};

export default GlobalSettings;
